// Web Push subscription for the logged-in agent. Registers this browser with
// the server so it can receive closed-tab notifications. No-op in dev (no
// service worker), when notification permission isn't granted, or when push is
// disabled server-side. Safe to call repeatedly — it reuses any existing
// subscription.

import { apiFetch } from './api';

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  // Back it with a concrete ArrayBuffer so it satisfies BufferSource (the
  // generic Uint8Array<ArrayBufferLike> default doesn't).
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export async function ensurePushSubscription(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  if (!('Notification' in window) || Notification.permission !== 'granted') return;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return; // no SW (dev) or not registered yet

    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      // 503 here = push disabled server-side; apiFetch throws → caught below.
      const { key } = await apiFetch<{ key: string }>('/api/push/key');
      if (!key) return;
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key) as BufferSource,
      });
    }

    const json = sub.toJSON() as {
      endpoint?: string;
      keys?: { p256dh?: string; auth?: string };
    };
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;
    await apiFetch('/api/push/subscribe', {
      method: 'POST',
      body: JSON.stringify({
        endpoint: json.endpoint,
        keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
      }),
    });
  } catch {
    /* push disabled / denied / offline — non-fatal */
  }
}
